import { defineStore } from "pinia";
import api from "@/services/api";

export interface Target {
  id: string;
  targetName: string;
  targetType: string;
  targetScopes?: string;
  targetFacilities?: object[];
  baseYear: string;
  targetYear: string;
  reductionPercentage: number;
  contributionPercentage?: number;
  facilityEmissionSource?: object;
  emissionGroup?: {
    groupName: string;
    groupNameLabel: string;
  };
}

export interface EmissionGroup {
  emission: number;
  emissionGroupName: string;
  emissionGroupId: string;
  percentage: number;
  scope: string;
}

export interface Projection {
  year: string;
  value: number;
}

type State = {
  isLoading: boolean;
  isTargetLoading: boolean;
  targets: Target[];
  BUSINESS_AS_USUAL: Projection[];
  SCIENCE_BASED: Projection[];
  INITIATIVES: Projection[];
  selectedFacilityId: string;
  reduction: number;
  emissionGroups: EmissionGroup[];
  scope: string;
  contribution: string;
  companyTargetYears: Projection[];
  selectedTargetIds: string[];
  selectedEmissionIds: string[];
  endYear: string;
  legends: string[];
  baseYear: string;
};

export const useTarget = defineStore("target", {
  state: (): State => ({
    isLoading: false,
    isTargetLoading: false,
    targets: [] as Target[],
    BUSINESS_AS_USUAL: [] as Projection[],
    SCIENCE_BASED: [] as Projection[],
    INITIATIVES: [] as Projection[],
    selectedFacilityId: "" as string,
    reduction: 0,
    emissionGroups: [] as EmissionGroup[],
    scope: "" as string,
    contribution: "" as string,
    companyTargetYears: [] as Projection[],
    selectedTargetIds: [],
    selectedEmissionIds: [],
    endYear: "" as string,
    legends: [
      "BASELINE",
      "BUSINESS_AS_USUAL",
      "SCIENCE_BASED",
      "INITIATIVES",
      "HISTORIC_EMISSIONS",
      "TARGET",
    ],
    baseYear: "",
  }),
  actions: {
    async addTarget(payload: object) {
      this.isLoading = true;
      const companyId = localStorage.getItem("companyId");

      try {
        await api.post(`company/${companyId}/target`, payload);
        this.isLoading = false;
      } catch (e) {
        this.isLoading = false;
      }
    },
    async fetchTargets() {
      this.isTargetLoading = true;
      const companyId = localStorage.getItem("companyId");

      let filter = `?baseYear=${this.baseYear}`;

      if (this.selectedFacilityId) {
        filter += `&facilityId=${this.selectedFacilityId}`;
      }

      try {
        const { data } = await api.get(`company/${companyId}/target${filter}`);
        this.targets = data;
        this.isTargetLoading = false;
      } catch (e) {
        this.isTargetLoading = false;
      }
    },
    async fetchCompanyTargetYears() {
      this.isLoading = true;
      let filter: string = `?baseYear=${this.baseYear}`;
      const companyId = localStorage.getItem("companyId");

      if (this.selectedTargetIds.length !== 0) {
        this.selectedTargetIds.map((id, index) => {
          filter += `&targetIdList=${id}`;
        });
      }

      if (this.selectedEmissionIds.length !== 0) {
        const firstSymbol = this.selectedTargetIds.length === 0 ? "?" : "&";
        this.selectedEmissionIds.map((id, index) => {
          filter += `&emissionGroupIdList=${id}`;
        });
      }

      if (this.selectedFacilityId) {
        filter += `&facilityId=${this.selectedFacilityId}`;
      }

      try {
        const { data } = await api.get(
          `/company/${companyId}/target/years${filter}`
        );
        this.companyTargetYears = data;
      } catch (e) {
        this.isLoading = false;
      }
    },
    async fetchProjection(projectionType: string) {
      this.isLoading = true;
      const companyId = localStorage.getItem("companyId");

      let filter = `?projectionType=${projectionType}&baseYear=${this.baseYear}`;

      if (this.endYear) {
        filter += `&endYear=${this.endYear}`;
      }

      if (this.selectedEmissionIds.length !== 0) {
        this.selectedEmissionIds.map((id) => {
          filter += `&emissionGroupIdList=${id}`;
        });
      }

      if (this.selectedFacilityId) {
        filter += `&facilityId=${this.selectedFacilityId}`;
      }

      try {
        const { data } = await api.get(
          `company/${companyId}/projection${filter}`
        );
        if (projectionType === "BUSINESS_AS_USUAL") {
          this.BUSINESS_AS_USUAL = data;
        }

        if (projectionType === "SCIENCE_BASED") {
          this.SCIENCE_BASED = data;
        }

        if (projectionType === "INITIATIVES") {
          this.INITIATIVES = data;
        }

        this.isLoading = false;
      } catch (e) {
        this.isLoading = false;
      }
    },
    async fetchCompanyOrFacilityProjection(type: string, targetId?: string) {
      const companyId = localStorage.getItem("companyId");
      let endpoint = `company/${companyId}/target/years?targetId=${targetId}`;

      if (type === "FACILTY") {
        endpoint = `${endpoint}&isFacility=true`;
      }

      try {
        const { data } = await api.get(endpoint);
        if (type === "COMPANY") {
          //this.companyProjection = data
        }
      } catch (e) {}
    },
    async getReduction(
      targetType: string,
      baseYear: string,
      targetYear: string
    ) {
      this.isLoading = true;
      const companyId = localStorage.getItem("companyId");

      let facilityId = "";

      if (this.selectedFacilityId) {
        facilityId += `&facilityId=${this.selectedFacilityId}`;
      }

      try {
        const { data } = await api.get(
          `company/${companyId}/reduction?targetType=${targetType}&baseYear=${baseYear}&targetYear=${targetYear}${facilityId}`
        );
        this.reduction = data.result;
        this.isLoading = false;
      } catch (e) {
        this.isLoading = false;
      }
    },

    async getContribution(facilityId: string) {
      const companyId = localStorage.getItem("companyId");

      let filter = "";

      if (this.selectedTargetIds.length !== 0) {
        this.selectedTargetIds.forEach((targetId) => {
          filter += `&targetIdList=${targetId}`;
        });
      }

      try {
        const { data } = await api.get(
          `/company/${companyId}/target/contribution?facilityId=${facilityId}`
        );
        this.contribution = data.result;
      } catch (e) {
        this.contribution = "";
      }
    },

    async fetchFacilityEmissionGroup(year: string, facilityId?: string) {
      this.isLoading = true;
      const companyId = localStorage.getItem("companyId");
      let filter = "";
      if (facilityId) {
        filter = `&facilityId=${facilityId}`;
      }

      try {
        const { data } = await api.get(
          `company/${companyId}/emission-group/percentage?year=${year}${filter}`
        );
        this.emissionGroups = data;
        this.isLoading = false;
      } catch (e) {
        this.isLoading = false;
      }
    },
    async deleteTarget(targetId: string) {
      this.isTargetLoading = true;
      const companyId = localStorage.getItem("companyId");

      await api
        .delete(`/company/${companyId}/target/${targetId}`)
        .then(() => {
          this.targets.splice(
            this.targets.map((t) => t.id).indexOf(targetId),
            1
          );
          this.isTargetLoading = false;
        })
        .catch(() => {
          this.isTargetLoading = false;
        });
    },
  },
  getters: {
    getTotalTargetPercentage: (state) => {
      return 0;
    },
    getTargets({ targets, selectedFacilityId }): Target[] {
      return targets;
    },
    filteredEmissionGroups({ emissionGroups, scope }): EmissionGroup[] {
      if (scope === "") return emissionGroups;

      return emissionGroups.filter((e) => e.scope === scope);
    },
    getMaxYear({
      companyTargetYears,
      selectedFacilityId,
      SCIENCE_BASED,
    }): string {
      if (!selectedFacilityId) {
        if (companyTargetYears.length === 0) return "";

        return companyTargetYears[companyTargetYears.length - 1].year;
      }

      if (companyTargetYears.length !== 0) {
        return Math.max(
          ...companyTargetYears.map((o) => Number(o.year))
        ).toString();
      }

      return SCIENCE_BASED[SCIENCE_BASED.length - 1].year;
    },
  },
});
